import React, { useMemo } from "react";
import {
    StandardModalHeader,
    StandardModalFooter,
    PlainModal,
} from "@design-system/Modal/Modal";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useEnableAccountsBulkMutation } from "@api/account.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { pluralizeWithCount } from "@common/helpers/string";
import { businessReachClause, useBusinessNamesFor } from "./reachCopy";

interface AccountRowConstituent {
    id: string;
    name: string;
    entityId: number;
}

interface EnableChartOfAccountsModalProps {
    show?: boolean;
    onHide?: () => void;
    accounts: AccountRowConstituent[];
    // Editor override: when set, confirm runs this (the draft fan-out) instead of
    // the chart page's `enableAccountsBulk` mutation.
    onConfirm?: () => void;
}

export const EnableChartOfAccountsModal: React.FC<
    EnableChartOfAccountsModalProps
> = ({ show, onHide, accounts, onConfirm }) => {
    const enableAccountsBulk = useEnableAccountsBulkMutation();
    const { toast, error: toastError } = useToaster();
    const businessNamesFor = useBusinessNamesFor();

    const uniqueEntityIds = useMemo(
        () => Array.from(new Set(accounts.map((account) => account.entityId))),
        [accounts],
    );

    const accountName = accounts[0]?.name ?? "";
    const reach = businessReachClause(businessNamesFor(uniqueEntityIds));

    const handleConfirm = async () => {
        if (onConfirm) {
            onConfirm();
            onHide?.();
            return;
        }
        const body = accounts.map((account) => ({
            accountId: account.id,
            entityId: account.entityId,
        }));
        try {
            const results = await enableAccountsBulk.mutateAsync(body);
            const successCount = results.filter((r) => r.success).length;
            const totalCount = results.length;
            if (successCount === totalCount) {
                toast(`Enabled ${pluralizeWithCount("account", successCount)}`);
            } else {
                const failedCount = totalCount - successCount;
                toast(
                    `Enabled ${pluralizeWithCount("account", successCount)}, ${failedCount} failed`,
                );
            }
            onHide?.();
        } catch {
            toastError("Failed to enable accounts");
        }
    };

    return (
        <PlainModal show={show} onHide={onHide} size="md">
            <StandardModalHeader>
                Are you sure you want to enable this account?
            </StandardModalHeader>
            <p>{`This will enable ${accountName} in ${reach}.`}</p>
            <StandardModalFooter className="mt-4">
                <Button
                    type="button"
                    variant="secondary"
                    onClick={onHide}
                    size="xl"
                    disabled={enableAccountsBulk.isPending}
                >
                    Cancel
                </Button>
                <ButtonWithLoader
                    variant="primary"
                    type="button"
                    loading={enableAccountsBulk.isPending}
                    size="xl"
                    onClick={handleConfirm}
                >
                    Enable
                </ButtonWithLoader>
            </StandardModalFooter>
        </PlainModal>
    );
};
